/**
 * The collection document, as the wire contract defines it.
 *
 * These types mirror `ImportExportBundleDTO` in the shared Swift package: the
 * server stores what the editor sends, and the phone decodes the published
 * bytes with the same shapes. Two details are easy to get wrong and both are
 * encoded here rather than in components:
 *
 * - a parameter value is **tagged** (`{"type":"int","value":2}`), not a bare
 *   JSON number;
 * - editing is immutable, so undo/redo (task 14.17) is a stack of documents.
 */

export type ParameterType = "integer" | "double" | "boolean" | "string";

export type ParameterValue =
  | { readonly type: "int"; readonly value: number }
  | { readonly type: "double"; readonly value: number }
  | { readonly type: "bool"; readonly value: boolean }
  | { readonly type: "string"; readonly value: string };

export interface ParameterConstraints {
  readonly min?: number | null | undefined;
  readonly max?: number | null | undefined;
  readonly allowed?: readonly string[] | null | undefined;
}

export interface Parameter {
  readonly id: string;
  readonly key: string;
  readonly type: ParameterType;
  readonly defaultValue?: ParameterValue | null | undefined;
  readonly constraints?: ParameterConstraints | null | undefined;
  readonly uiHint?: string | null | undefined;
}

export type RuleType =
  | "include"
  | "includeQuick"
  | "exclude"
  | "override"
  | "conditional"
  | "includeGroup"
  | "excludeGroup";

export interface RulePredicate {
  readonly parameterKey: string;
  readonly op: "==" | "!=" | ">" | ">=" | "<" | "<=";
  readonly value: ParameterValue;
}

export interface TemplateRule {
  readonly id: string;
  readonly type: RuleType;
  readonly targetDefinitionID?: string | null | undefined;
  readonly targetGroup?: string | null | undefined;
  readonly conditionExpression?: string | null | undefined;
  readonly quantityExpression?: string | null | undefined;
  readonly quickTitle?: string | null | undefined;
  readonly quickKind?: "gear" | "task" | "purchase" | null | undefined;
  readonly quickNotes?: string | null | undefined;
  readonly overrideTitle?: string | null | undefined;
  readonly overrideNotes?: string | null | undefined;
  readonly overrideWeight?: number | null | undefined;
  readonly overrideTags?: readonly string[] | null | undefined;
  readonly groupTags?: readonly string[] | null | undefined;
  readonly groupMatchAll?: boolean | null | undefined;
  readonly predicate?: RulePredicate | null | undefined;
  readonly conditionalRules?: readonly TemplateRule[] | null | undefined;
  readonly note?: string | null | undefined;
}

export interface Template {
  readonly id: string;
  readonly title: string;
  readonly description?: string | null | undefined;
  readonly parentIDs: readonly string[];
  readonly parameters: readonly Parameter[];
  readonly rules: readonly TemplateRule[];
}

export interface BundleItem {
  readonly id: string;
  readonly title: string;
  readonly kind: "gear" | "task" | "purchase";
  readonly defaultWeight?: number | null | undefined;
  readonly tags: readonly string[];
  readonly notes?: string | null | undefined;
}

export interface CollectionDocument {
  readonly schemaVersion: 1;
  readonly name?: string | null | undefined;
  readonly description?: string | null | undefined;
  readonly items: readonly BundleItem[];
  readonly templates: readonly Template[];
}

export function emptyDocument(name: string): CollectionDocument {
  return { schemaVersion: 1, name, items: [], templates: [] };
}

/** Crypto-random, because ids end up in published bundles. */
export function newId(): string {
  return crypto.randomUUID();
}

export function templateById(document: CollectionDocument, id: string): Template | undefined {
  return document.templates.find((template) => template.id === id);
}

export function withTemplate(document: CollectionDocument, template: Template): CollectionDocument {
  const exists = document.templates.some((candidate) => candidate.id === template.id);
  return {
    ...document,
    templates: exists
      ? document.templates.map((candidate) => (candidate.id === template.id ? template : candidate))
      : [...document.templates, template],
  };
}

export function withoutTemplate(document: CollectionDocument, id: string): CollectionDocument {
  return {
    ...document,
    templates: document.templates
      .filter((template) => template.id !== id)
      // Inheritance may not dangle: a parent that is gone would resolve to
      // nothing on the subscriber's phone, silently.
      .map((template) => ({ ...template, parentIDs: template.parentIDs.filter((parent) => parent !== id) })),
  };
}

export function newTemplate(title: string): Template {
  return { id: newId(), title, parentIDs: [], parameters: [], rules: [] };
}

export function newParameter(): Parameter {
  return { id: newId(), key: "", type: "integer", defaultValue: { type: "int", value: 1 } };
}

/** The default value a type starts with, so a switch never leaves a mismatch. */
export function defaultValueFor(type: ParameterType): ParameterValue {
  switch (type) {
    case "integer":
      return { type: "int", value: 1 };
    case "double":
      return { type: "double", value: 1 };
    case "boolean":
      return { type: "bool", value: false };
    case "string":
      return { type: "string", value: "" };
  }
}

export function parameterValueText(value: ParameterValue | null | undefined): string {
  if (!value) return "";
  return value.type === "bool" ? String(value.value) : String(value.value);
}

/** Parses editor input back into the tagged wire shape; null when unusable. */
export function parseParameterValue(type: ParameterType, raw: string): ParameterValue | null {
  const trimmed = raw.trim();
  switch (type) {
    case "integer": {
      if (!/^-?\d+$/.test(trimmed)) return null;
      return { type: "int", value: Number(trimmed) };
    }
    case "double": {
      const parsed = Number(trimmed);
      if (trimmed === "" || Number.isNaN(parsed) || !Number.isFinite(parsed)) return null;
      return { type: "double", value: parsed };
    }
    case "boolean":
      return { type: "bool", value: trimmed === "true" };
    case "string":
      return { type: "string", value: raw };
  }
}

/** Which templates a template may inherit from: this collection's, minus cycles. */
export function eligibleParents(document: CollectionDocument, templateID: string): readonly Template[] {
  const descendants = new Set<string>([templateID]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const template of document.templates) {
      if (descendants.has(template.id)) continue;
      if (template.parentIDs.some((parent) => descendants.has(parent))) {
        descendants.add(template.id);
        grew = true;
      }
    }
  }
  // Inheritance stays inside the collection (the publish gate refuses anything
  // else), and a template cannot inherit from its own descendant.
  return document.templates.filter((template) => !descendants.has(template.id));
}

/** Parameter keys visible to a template's rules, including inherited ones. */
export function mergedParameters(document: CollectionDocument, templateID: string): readonly Parameter[] {
  const seen = new Set<string>();
  const ordered: Parameter[] = [];
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    const template = templateById(document, id);
    if (!template) return;
    for (const parent of template.parentIDs) visit(parent);
    for (const parameter of template.parameters) {
      const index = ordered.findIndex((candidate) => candidate.key.toLowerCase() === parameter.key.toLowerCase());
      if (index >= 0) ordered[index] = parameter;
      else ordered.push(parameter);
    }
  };
  visit(templateID);
  return ordered;
}
